/**
 * Directory service - profiles + tournament-scoped role assignments.
 *
 * The directory is the persistent backbone: a Profile is identity, a
 * RoleAssignment binds it to a tournament/team for a finite window.
 */

import type {
  ContactPreferences,
  DirectoryStatus,
  Id,
  PreferredContactMethod,
  Profile,
  ProfileAttributeValue,
  Role,
  RoleAssignment,
} from '../models';
import { getSmsConsentStatus } from './communicationConsentService';
import { getDb, updateDb, type AppDatabase } from './store';
import { newId, nowIso } from './ids';

export const DIRECTORY_ATTRIBUTE_FIELDS = [
  { key: 'school', label: 'School' },
  { key: 'ageGroup', label: 'Age group' },
  { key: 'position', label: 'Position' },
  { key: 'shotHand', label: 'Shot hand' },
  { key: 'skillLevel', label: 'Skill level' },
  { key: 'officialCertification', label: 'Official certification' },
  { key: 'availability', label: 'Availability' },
  { key: 'guardianName', label: 'Guardian name' },
  { key: 'guardianEmail', label: 'Guardian email' },
  { key: 'guardianPhone', label: 'Guardian phone' },
  { key: 'emergencyContact', label: 'Emergency contact' },
] as const;

export type CommunicationChannel = 'email' | 'sms' | 'in_app' | 'push';

export interface ContactReadiness {
  status: 'ready' | 'warning' | 'blocked';
  label: string;
  issues: string[];
  availableChannels: CommunicationChannel[];
}

export interface ProfileRelationships {
  roleAssignments: RoleAssignment[];
  playerIds: Id[];
  rosterEntries: AppDatabase['rosterEntries'];
  teams: AppDatabase['teams'];
  tournaments: AppDatabase['tournaments'];
  registrations: AppDatabase['registrations'];
  gameAssignments: AppDatabase['gameAssignments'];
  officialAssignments: AppDatabase['officialAssignments'];
  waivers: AppDatabase['waivers'];
  paymentIntents: AppDatabase['paymentIntents'];
}

// --------------------------------------------------------------------------
// Reads
// --------------------------------------------------------------------------

export function normalizeProfile(profile: Profile): Profile {
  return {
    ...profile,
    directoryStatus: profile.directoryStatus ?? 'active',
    preferredContactMethod: profile.preferredContactMethod ?? preferredFromContact(profile),
    contactPreferences: profile.contactPreferences ?? {},
    attributes: profile.attributes ?? {},
    source: profile.source ?? 'manual',
    tags: profile.tags ?? [],
  };
}

export function listProfiles(options: { includeArchived?: boolean } = {}): Profile[] {
  return [...getDb().profiles]
    .map(normalizeProfile)
    .filter((p) => options.includeArchived || p.directoryStatus !== 'archived')
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

export function getProfile(id: Id): Profile | undefined {
  const profile = getDb().profiles.find((p) => p.id === id);
  return profile ? normalizeProfile(profile) : undefined;
}

export function findProfileByEmail(email?: string | null): Profile | undefined {
  if (!email) return undefined;
  const norm = normalizeEmail(email);
  const profile = getDb().profiles.find((p) => normalizeEmail(p.email) === norm);
  return profile ? normalizeProfile(profile) : undefined;
}

export function findProfileByName(name: string): Profile | undefined {
  const norm = normalizeName(name);
  const profile = getDb().profiles.find((p) => normalizeName(p.displayName) === norm);
  return profile ? normalizeProfile(profile) : undefined;
}

export function listRoleAssignments(tournamentId: Id): RoleAssignment[] {
  return getDb().roleAssignments.filter((a) => a.tournamentId === tournamentId);
}

export function listAssignmentsForProfile(profileId: Id): RoleAssignment[] {
  return getDb().roleAssignments.filter((a) => a.profileId === profileId);
}

export function listProfilesForRole(tournamentId: Id, role: Role): Profile[] {
  const ids = new Set(
    listRoleAssignments(tournamentId)
      .filter((a) => a.role === role)
      .map((a) => a.profileId),
  );
  return listProfiles().filter((p) => ids.has(p.id));
}

export function getContactReadiness(
  profileInput: Profile,
  channels: CommunicationChannel[] = [],
): ContactReadiness {
  const profile = normalizeProfile(profileInput);
  const preferences = profile.contactPreferences ?? {};
  const issues: string[] = [];
  const availableChannels: CommunicationChannel[] = [];

  if (profile.directoryStatus === 'archived') {
    return {
      status: 'blocked',
      label: 'Archived',
      issues: ['Archived profiles are excluded from communication audiences.'],
      availableChannels,
    };
  }

  if (profile.directoryStatus === 'inactive') {
    issues.push('Profile is inactive.');
  }
  if (profile.preferredContactMethod === 'none') {
    issues.push('Preferred contact method is set to none.');
  }

  if (profile.email && !preferences.emailOptOut) availableChannels.push('email');
  const smsConsent = getSmsConsentStatus(profile);
  if (smsConsent.operational.state === 'opted_in') availableChannels.push('sms');
  if (!preferences.inAppOptOut) availableChannels.push('in_app');

  const wanted: CommunicationChannel[] = channels.length > 0 ? channels : ['email', 'sms', 'in_app'];
  const missingSelected = wanted.filter((channel) => !availableChannels.includes(channel));

  for (const channel of missingSelected) {
    if (channel === 'email') issues.push(profile.email ? 'Email opt-out is enabled.' : 'Missing email address.');
    if (channel === 'sms') {
      if (smsConsent.operational.state === 'no_phone') issues.push('Missing phone number.');
      else if (smsConsent.operational.state === 'invalid_phone') issues.push('Invalid phone number.');
      else if (smsConsent.operational.state === 'opted_out') issues.push('SMS opt-out is recorded.');
      else if (smsConsent.operational.state === 'phone_changed') issues.push('Phone number changed since SMS consent was recorded.');
      else issues.push('Registration SMS consent is not recorded.');
    }
    if (channel === 'in_app') issues.push('In-app messaging opt-out is enabled.');
    if (channel === 'push') issues.push('Push delivery is not configured for this profile.');
  }

  const hasRequestedChannel = wanted.some((channel) => availableChannels.includes(channel));
  if (!hasRequestedChannel) {
    return {
      status: 'blocked',
      label: 'Unreachable',
      issues: unique(issues.length > 0 ? issues : ['No selected communication channel is available.']),
      availableChannels,
    };
  }

  if (issues.length > 0) {
    return {
      status: 'warning',
      label: 'Needs attention',
      issues: unique(issues),
      availableChannels,
    };
  }

  return { status: 'ready', label: 'Ready', issues: [], availableChannels };
}

export function getProfileRelationships(profileId: Id, db = getDb()): ProfileRelationships {
  const playerIds = db.players.filter((p) => p.profileId === profileId).map((p) => p.id);
  const playerIdSet = new Set(playerIds);
  const rosterEntries = db.rosterEntries.filter((r) => playerIdSet.has(r.playerId));
  const teamIds = new Set(rosterEntries.map((r) => r.teamId));
  const roleAssignments = db.roleAssignments.filter((a) => a.profileId === profileId);
  const tournamentIds = new Set([
    ...rosterEntries.map((r) => r.tournamentId),
    ...roleAssignments.map((a) => a.tournamentId),
    ...db.registrations.filter((r) => r.profileId === profileId).map((r) => r.tournamentId),
  ]);
  const registrationIds = new Set(db.registrations.filter((r) => r.profileId === profileId).map((r) => r.id));

  return {
    roleAssignments,
    playerIds,
    rosterEntries,
    teams: db.teams.filter((t) => teamIds.has(t.id) || t.captainProfileId === profileId),
    tournaments: db.tournaments.filter((t) => tournamentIds.has(t.id)),
    registrations: db.registrations.filter((r) => r.profileId === profileId),
    gameAssignments: db.gameAssignments.filter((a) => a.profileId === profileId),
    officialAssignments: db.officialAssignments.filter((a) => db.officials.some((o) => o.id === a.officialId && o.profileId === profileId)),
    waivers: db.waivers.filter((w) => w.profileId === profileId),
    paymentIntents: db.paymentIntents.filter((p) => p.registrationId ? registrationIds.has(p.registrationId) : false),
  };
}

export function findDuplicateProfileCandidates(profileInput: Profile, db = getDb()): Profile[] {
  const profile = normalizeProfile(profileInput);
  const email = normalizeEmail(profile.email);
  const phone = normalizePhone(profile.phone);
  const name = normalizeName(profile.displayName);

  return db.profiles
    .filter((candidate) => candidate.id !== profile.id)
    .map(normalizeProfile)
    .filter((candidate) => {
      if (email && normalizeEmail(candidate.email) === email) return true;
      if (phone && normalizePhone(candidate.phone) === phone) return true;
      return name.length >= 4 && normalizeName(candidate.displayName) === name;
    })
    .sort((a, b) => a.displayName.localeCompare(b.displayName));
}

// --------------------------------------------------------------------------
// Mutations
// --------------------------------------------------------------------------

export interface UpsertProfileInput {
  displayName: string;
  email?: string;
  phone?: string;
  pronouns?: string;
  bio?: string;
  defaultRole?: Role;
  globalRoles?: Role[];
  directoryStatus?: DirectoryStatus;
  preferredContactMethod?: PreferredContactMethod;
  contactPreferences?: ContactPreferences;
  attributes?: Record<string, ProfileAttributeValue>;
  source?: string;
  tags?: string[];
  lastVerifiedAt?: string;
  archivedAt?: string;
}

export function createProfile(input: UpsertProfileInput): Profile {
  const ts = nowIso();
  const profile: Profile = normalizeProfile({
    id: newId('user'),
    kind: 'profile',
    createdAt: ts,
    updatedAt: ts,
    displayName: input.displayName.trim(),
    email: cleanEmailAddress(input.email),
    phone: cleanPhoneNumber(input.phone),
    pronouns: cleanString(input.pronouns),
    bio: cleanString(input.bio),
    defaultRole: input.defaultRole ?? 'player',
    globalRoles: sanitizeRoles(input.globalRoles, input.defaultRole ?? 'player'),
    directoryStatus: input.directoryStatus ?? 'active',
    preferredContactMethod: input.preferredContactMethod ?? 'email',
    contactPreferences: input.contactPreferences ?? {},
    attributes: input.attributes ?? {},
    source: cleanString(input.source) ?? 'manual',
    tags: sanitizeTags(input.tags),
    lastVerifiedAt: input.lastVerifiedAt,
    archivedAt: input.archivedAt,
  });
  updateDb((db) => ({ ...db, profiles: [...db.profiles, profile] }));
  return profile;
}

export function updateProfile(id: Id, patch: Partial<UpsertProfileInput>): Profile | undefined {
  let updated: Profile | undefined;
  updateDb((db) => ({
    ...db,
    profiles: db.profiles.map((p) => {
      if (p.id !== id) return p;
      const current = normalizeProfile(p);
      const next = normalizeProfile({
        ...current,
        ...patch,
        displayName: (patch.displayName ?? current.displayName).trim(),
        email: patch.email !== undefined ? cleanEmailAddress(patch.email) : current.email,
        phone: patch.phone !== undefined ? cleanPhoneNumber(patch.phone) : current.phone,
        pronouns: patch.pronouns !== undefined ? cleanString(patch.pronouns) : current.pronouns,
        bio: patch.bio !== undefined ? cleanString(patch.bio) : current.bio,
        defaultRole: patch.defaultRole ?? current.defaultRole,
        globalRoles: patch.globalRoles ? sanitizeRoles(patch.globalRoles, patch.defaultRole ?? current.defaultRole) : current.globalRoles,
        contactPreferences: patch.contactPreferences ?? current.contactPreferences,
        attributes: patch.attributes ?? current.attributes,
        source: patch.source !== undefined ? cleanString(patch.source) ?? 'manual' : current.source,
        tags: patch.tags ? sanitizeTags(patch.tags) : current.tags,
        updatedAt: nowIso(),
      });
      updated = {
        ...next,
        contactPreferences: {
          ...(next.contactPreferences ?? {}),
          smsOptIn: getSmsConsentStatus(next, db.communicationConsents).operational.state === 'opted_in',
          smsMarketingOptIn: getSmsConsentStatus(next, db.communicationConsents).marketing.state === 'opted_in',
          smsOptOut: getSmsConsentStatus(next, db.communicationConsents).operational.state === 'opted_out',
        },
      };
      return updated;
    }),
  }));
  return updated;
}

/** Find an existing profile by email/name or create a fresh one. */
export function upsertProfile(input: UpsertProfileInput): Profile {
  const existing = findProfileByEmail(input.email) ?? findProfileByName(input.displayName);
  if (existing) return updateProfile(existing.id, input) ?? existing;
  return createProfile(input);
}

export function upsertRegistrationProfile(input: {
  displayName: string;
  email: string;
  phone?: string;
  attributes?: Record<string, ProfileAttributeValue>;
}): Profile {
  const email = normalizeEmail(input.email);
  const existing = findProfileByEmail(email);

  if (existing) {
    if (hasPrivilegedRole(existing)) return existing;
    return updateProfile(existing.id, {
      displayName: input.displayName.trim() || existing.displayName,
      email,
      phone: cleanString(input.phone) ?? existing.phone,
      defaultRole: existing.defaultRole ?? 'player',
      globalRoles: uniqueRoles([...existing.globalRoles, 'player']),
      directoryStatus: 'active',
      preferredContactMethod: existing.preferredContactMethod ?? 'email',
      attributes: {
        ...(existing.attributes ?? {}),
        ...(input.attributes ?? {}),
      },
      source: 'registration',
      tags: unique([...(existing.tags ?? []), 'registered']),
      lastVerifiedAt: nowIso(),
      archivedAt: undefined,
    }) ?? existing;
  }

  return createProfile({
    displayName: input.displayName.trim(),
    email,
    phone: cleanString(input.phone),
    defaultRole: 'player',
    globalRoles: ['player'],
    directoryStatus: 'active',
    preferredContactMethod: 'email',
    contactPreferences: {},
    attributes: input.attributes ?? {},
    source: 'registration',
    tags: ['registered'],
    lastVerifiedAt: nowIso(),
  });
}

export function updateProfileAttributes(
  id: Id,
  patch: Record<string, ProfileAttributeValue | undefined>,
): Profile | undefined {
  const profile = getProfile(id);
  if (!profile) return undefined;
  const attributes = { ...(profile.attributes ?? {}) };
  for (const [key, value] of Object.entries(patch)) {
    const cleanKey = key.trim();
    if (!cleanKey) continue;
    if (value === undefined || value === null || value === '') delete attributes[cleanKey];
    else attributes[cleanKey] = value;
  }
  return updateProfile(id, { attributes });
}

export function archiveProfile(id: Id): Profile | undefined {
  return updateProfile(id, { directoryStatus: 'archived', archivedAt: nowIso() });
}

export function deactivateProfile(id: Id): Profile | undefined {
  return updateProfile(id, { directoryStatus: 'inactive', archivedAt: undefined });
}

export function reactivateProfile(id: Id): Profile | undefined {
  return updateProfile(id, { directoryStatus: 'active', archivedAt: undefined });
}

/** Compatibility alias: directory UI now archives instead of hard-deleting. */
export function deleteProfile(id: Id): void {
  archiveProfile(id);
}

// --------------------------------------------------------------------------
// Role assignments
// --------------------------------------------------------------------------

export interface AssignRoleInput {
  profileId: Id;
  tournamentId: Id;
  role: Role;
  teamId?: Id;
}

export function assignRole(input: AssignRoleInput): RoleAssignment {
  const ts = nowIso();
  const assignment: RoleAssignment = {
    id: newId('ra'),
    kind: 'role-assignment',
    createdAt: ts,
    updatedAt: ts,
    profileId: input.profileId,
    tournamentId: input.tournamentId,
    role: input.role,
    teamId: input.teamId,
  };
  updateDb((db) => {
    const existing = db.roleAssignments.find(
      (a) =>
        a.profileId === input.profileId &&
        a.tournamentId === input.tournamentId &&
        a.role === input.role &&
        a.teamId === input.teamId,
    );
    if (existing) return db;
    return { ...db, roleAssignments: [...db.roleAssignments, assignment] };
  });
  return assignment;
}

export function removeRoleAssignment(id: Id): void {
  updateDb((db) => ({
    ...db,
    roleAssignments: db.roleAssignments.filter((a) => a.id !== id),
  }));
}

export function removeProfileFromTournament(profileId: Id, tournamentId: Id): void {
  updateDb((db) => ({
    ...db,
    roleAssignments: db.roleAssignments.filter(
      (a) => !(a.profileId === profileId && a.tournamentId === tournamentId),
    ),
    rosterEntries: db.rosterEntries.filter((r) => {
      if (r.tournamentId !== tournamentId) return true;
      const player = db.players.find((p) => p.id === r.playerId);
      return player?.profileId !== profileId;
    }),
  }));
}

function preferredFromContact(profile: Profile): PreferredContactMethod {
  if (profile.email) return 'email';
  if (profile.phone) return 'sms';
  return 'in_app';
}

function cleanString(value?: string): string | undefined {
  const out = value?.trim();
  return out || undefined;
}

function cleanEmailAddress(value?: string): string | undefined {
  const email = value?.replace(/\s+/g, '').toLowerCase() ?? '';
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email) ? email : undefined;
}

function cleanPhoneNumber(value?: string): string | undefined {
  const trimmed = value?.trim() ?? '';
  const prefix = trimmed.startsWith('+') ? '+' : '';
  const phone = `${prefix}${trimmed.replace(/\D+/g, '').slice(0, 15)}`;
  const digits = phone.replace(/\D+/g, '');
  return digits.length >= 10 && digits.length <= 15 ? phone : undefined;
}

function sanitizeRoles(roles: Role[] | undefined, fallback: Role): Role[] {
  const out = Array.from(new Set(roles && roles.length > 0 ? roles : [fallback]));
  return out.length > 0 ? out : [fallback];
}

function sanitizeTags(tags: string[] | undefined): string[] {
  return Array.from(new Set((tags ?? []).map((tag) => tag.trim()).filter(Boolean)));
}

function uniqueRoles(roles: Role[]): Role[] {
  return Array.from(new Set(roles));
}

function hasPrivilegedRole(profile: Profile): boolean {
  return [profile.defaultRole, ...profile.globalRoles].some((role) => role === 'admin' || role === 'staff' || role === 'scorekeeper' || role === 'referee');
}

function normalizeEmail(email?: string | null): string {
  return email?.trim().toLowerCase() ?? '';
}

function normalizePhone(phone?: string | null): string {
  return phone?.replace(/\D+/g, '') ?? '';
}

function normalizeName(name?: string | null): string {
  return name?.trim().toLowerCase().replace(/\s+/g, ' ') ?? '';
}

function unique(values: string[]): string[] {
  return Array.from(new Set(values));
}
